import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton, RevealGroup } from '@/components/atoms';
import { SectionHeading } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import { anchorOffsetSx } from '@/theme';

import { CATALOGUE_ID, CATALOGUE_TITLE_ID, COMPARE_ID, ServiceCountWord } from '../content';
import { useServiceCatalogue } from '../hooks/useServiceCatalogue';
import { ServiceCard, ServicePillarRail } from '../molecules';

/**
 * The services, grouped into the three stages of an import.
 *
 * The band's shape is the argument it is making. A rail on the left names the
 * three stages and marks the one you are in; the services sit to its right in
 * the stage they belong to. Read down, it says that importing a car has an
 * order, and that these services are what that order is made of — which is
 * the one thing a flat grid cannot say, and the reason the home strip and the
 * /about band both read as inventories rather than as an offer.
 *
 * The rail is only a rail from `lg`. Below that it would be a stack of three
 * links above the content it links to, a screen apart from any of it, so the
 * pillar headings carry the structure on their own and the rail is not drawn.
 *
 * It closes with a link down to `ServiceCompareBand`, which is the band that
 * answers the question this one raises. A reader who has just been shown
 * seven cards grouped by stage knows the shape of the offer and still cannot
 * tell which rows cost them anything; sending them to the table is cheaper
 * than widening these cards until they could carry it.
 */
export function ServiceCatalogueBand() {
  const { pillars, anchorIds } = useServiceCatalogue();
  const activeId = useScrollSpy(anchorIds);

  return (
    <Section
      id={CATALOGUE_ID}
      labelledBy={CATALOGUE_TITLE_ID}
      // The anchor the opening band's button points at. Without it the sticky
      // header lands on top of the heading the jump was aimed at.
      sx={anchorOffsetSx(96, 96)}
    >
      <SectionHeading
        id={CATALOGUE_TITLE_ID}
        eyebrow="What we do"
        title={`${ServiceCountWord} services, in the order you meet them`}
        lede="Most people need three or four of these. They are listed separately because they are sold separately — and grouped because when you need several, one team runs them."
      />

      <Grid container spacing={{ xs: 10, lg: 12 }}>
        <Grid size={{ xs: 12, lg: 3 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
          <ServicePillarRail pillars={pillars} activeId={activeId} />
        </Grid>

        <Grid size={{ xs: 12, lg: 9 }}>
          <Stack spacing={{ xs: 12, md: 16 }}>
            {pillars.map((pillar) => (
              <Box key={pillar.id} id={pillar.anchorId} sx={anchorOffsetSx(120, 120)}>
                <Stack spacing={3} sx={{ mb: { xs: 6, md: 8 } }}>
                  <Typography
                    aria-hidden
                    variant="caption"
                    sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'primary.main' }}
                  >
                    {pillar.step}
                  </Typography>
                  <Typography variant="h4" component="h3">
                    {pillar.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '58ch' }}>
                    {pillar.text}
                  </Typography>
                </Stack>

                <RevealGroup
                  component="ul"
                  step={60}
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    display: 'grid',
                    gap: { xs: 4, md: 5 },
                    // Two across, never three. A pillar holds two or three
                    // services, so a three-column track would leave the
                    // two-service pillar with a hole in it and size every card
                    // for the narrowest case.
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  {pillar.services.map((service) => (
                    <Box component="li" key={service.slug} sx={{ display: 'flex' }}>
                      <ServiceCard service={service} detailed />
                    </Box>
                  ))}
                </RevealGroup>
              </Box>
            ))}
          </Stack>

          <Box sx={{ mt: { xs: 10, md: 12 } }}>
            <AppButton to={`#${COMPARE_ID}`} appVariant="secondary">
              Compare them side by side
            </AppButton>
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
}
